import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmail, onAuthStateChange } from './firebase/auth';
import { resolveAuthContext } from './firebase/userProfile';
import './App.css';

function AdminLogin() {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null); // eslint-disable-line no-unused-vars
  const [authChecked, setAuthChecked] = useState(false);

  // If already authenticated, redirect by role (only when profile is valid to avoid redirect loop)
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setAuthChecked(true);
        return;
      }
      try {
        const ctx = await resolveAuthContext(currentUser.uid, currentUser.email || '');
        if (ctx.role === 'super_admin') {
          navigate('/super-admin', { replace: true });
          return;
        }
        if (ctx.role === 'tenant_admin' && ctx.tenantId) {
          navigate('/dashboard', { replace: true });
          return;
        }
        // Profile missing or not fully set up (e.g. no tenantId for tenant_admin) – stay on login, show message
        setErrors({ general: 'Your account is not set up. Add your user profile in Firebase (Realtime Database → users → your UID) with role and tenantId.' });
      } catch (_) {
        setErrors({ general: 'Could not load your profile. Check that users/your-uid exists in the database with role and email.' });
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!loginData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(loginData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!loginData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (loginData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      console.log('Firebase admin login attempt:', loginData.email);
      
      // Use Firebase authentication
      const result = await signInWithEmail(loginData.email, loginData.password);
      
      if (result.success) {
        console.log('Admin login successful:', result.user.email);
        const ctx = await resolveAuthContext(result.user.uid, result.user.email || '');
        setLoginData({ email: '', password: '' });
        setErrors({});
        if (ctx.role === 'super_admin') {
          navigate('/super-admin', { replace: true });
        } else if (ctx.role === 'tenant_admin' && ctx.tenantId) {
          navigate('/dashboard', { replace: true });
        } else {
          setErrors({ general: 'Your account is not set up. In Firebase Realtime Database → users → your UID, add role (super_admin or tenant_admin) and, for tenant_admin, tenantId.' });
        }
      } else {
        // Show Firebase error message
        setErrors({ general: result.message });
        console.error('Login failed:', result.error);
      }
    } catch (error) {
      console.error('Unexpected error during login:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      {/* Header */}
      <header className="admin-login-header">
        <div className="logo">
          <div className="logo-icon">💪</div>
          <span className="logo-text">DE MUSCLE FACTORY</span>
        </div>
      </header>

      {/* Login Form */}
      <div className="admin-login-wrapper">
        <div className="admin-login-card">
          <div className="admin-login-header-content">
            <h1 className="admin-login-title">Admin Login</h1>
            <p className="admin-login-subtitle">Access the administrative dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login-form">
            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={loginData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter admin email"
                disabled={isLoading}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={loginData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter admin password"
                disabled={isLoading}
              />
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={`admin-login-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner"></span>
                  Signing In...
                </>
              ) : (
                'Sign In as Admin'
              )}
            </button>
          </form>

          <div className="admin-login-footer">
            <p className="security-notice">
              🔒 This is a secure admin area. Only authorized personnel allowed.
            </p>
            <a href="/" className="back-to-home">
              ← Back to Homepage
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="admin-login-footer-section">
        <div className="container">
          <p>&copy; 2025 DE MUSCLE FACTORY. Admin Portal.</p>
        </div>
      </footer>
    </div>
  );
}

export default AdminLogin;